import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import {
  Bell,
  CalendarDays,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Plus,
  X,
} from 'lucide-react';
import { biAPI } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { useStoreContextStore } from '../../store/storeContextStore';
import {
  currentBsPeriod,
  formatAdDateKey,
  formatBsPeriod,
  shiftBsPeriod,
} from '../../utils/bsPeriod';
import { biPanel } from './biTheme';

const WEEKDAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

const COLOR_META = {
  purple: {
    key: 'purple',
    label: 'Meeting',
    dot: 'bg-[#7C5CFC]',
    pill: 'bg-[#EDE7FF] text-[#5B3FD6]',
    selectedPill: 'bg-white/20 text-white',
  },
  blue: {
    key: 'blue',
    label: 'Work',
    dot: 'bg-[#4A69E2]',
    pill: 'bg-[#E8EEFF] text-[#3553C8]',
    selectedPill: 'bg-white/20 text-white',
  },
  yellow: {
    key: 'yellow',
    label: 'Design',
    dot: 'bg-[#E8B923]',
    pill: 'bg-[#FFF6D6] text-[#9A7A10]',
    selectedPill: 'bg-white/20 text-white',
  },
  green: {
    key: 'green',
    label: 'Development',
    dot: 'bg-[#34C759]',
    pill: 'bg-[#E5F8EA] text-[#1F8A3B]',
    selectedPill: 'bg-white/20 text-white',
  },
  pink: {
    key: 'pink',
    label: 'Review',
    dot: 'bg-[#E85D9A]',
    pill: 'bg-[#FCE7F1] text-[#C23A74]',
    selectedPill: 'bg-white/20 text-white',
  },
  orange: {
    key: 'orange',
    label: 'Personal',
    dot: 'bg-[#F3A060]',
    pill: 'bg-[#FFEADB] text-[#C46A2B]',
    selectedPill: 'bg-white/20 text-white',
  },
};

const LEGEND = Object.values(COLOR_META);

const normalizeTaskColor = (value) => {
  const color = String(value || '').trim().toLowerCase();
  if (color === 'orange') return 'orange';
  return COLOR_META[color] ? color : 'blue';
};

const getColorMeta = (value) => COLOR_META[normalizeTaskColor(value)] || COLOR_META.blue;

const parseDateKey = (dateKey) => {
  const [year, month, day] = String(dateKey || '').split('-').map(Number);
  return new Date(year, (month || 1) - 1, day || 1);
};

const shiftAdDateKey = (dateKey, days) => {
  const next = parseDateKey(dateKey);
  next.setDate(next.getDate() + days);
  return formatAdDateKey(next);
};

const buildBsMonthGrid = (days = []) => {
  if (!days.length) return [];

  const firstAd = parseDateKey(days[0].adDate);
  const startOffset = firstAd.getDay();
  const cells = [];

  for (let i = 0; i < startOffset; i += 1) {
    cells.push({ key: `pad-start-${i}`, inMonth: false, bsDay: null, adDate: null });
  }

  days.forEach((day) => {
    cells.push({
      key: day.adDate,
      inMonth: true,
      bsDay: day.bsDay,
      adDate: day.adDate,
      bsDate: day.bsDate,
    });
  });

  while (cells.length % 7 !== 0) {
    cells.push({ key: `pad-end-${cells.length}`, inMonth: false, bsDay: null, adDate: null });
  }

  return cells;
};

const defaultTaskForm = (dateKey) => ({
  title: '',
  description: '',
  scheduledDate: dateKey,
  scheduledEndDate: dateKey,
});

const visibleDayTasks = (tasks = []) => (tasks || []).filter((task) => task.status !== 'cancelled');

const daysUntilDateKey = (fromDateKey, toDateKey) => {
  const from = parseDateKey(fromDateKey);
  const to = parseDateKey(toDateKey);
  const diffMs = to.getTime() - from.getTime();
  return Math.max(0, Math.round(diffMs / (24 * 60 * 60 * 1000)));
};

const formatDaysLeftLabel = (daysLeft) => {
  if (daysLeft === 0) return 'Due today';
  if (daysLeft === 1) return '1 day left';
  return `${daysLeft} days left`;
};

const formatTaskDateRange = (task) => {
  const start = task?.scheduledDate || '';
  const end = task?.scheduledEndDate || start;
  if (!start) return '—';
  return start === end ? start : `${start} → ${end}`;
};

const taskDueDate = (task) => task?.scheduledEndDate || task?.scheduledDate || '';

const BiCalendar = () => {
  const queryClient = useQueryClient();
  const { hasFeatureAccess } = useAuthStore();
  const selectedStore = useStoreContextStore((state) => state.selectedStore);
  const storeInfoId = selectedStore?.storeInfoId || selectedStore?.store_info_id || '';
  const canManage = hasFeatureAccess('bi.calendar');

  const todayKey = formatAdDateKey(new Date());
  const maxRescheduleDate = shiftAdDateKey(todayKey, 30);

  const [bsPeriod, setBsPeriod] = useState(currentBsPeriod());
  const [selectedDate, setSelectedDate] = useState(todayKey);
  const [showModal, setShowModal] = useState(false);
  const [taskForm, setTaskForm] = useState(defaultTaskForm(todayKey));
  const [actionTask, setActionTask] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState(todayKey);

  const { data, isLoading, isError, error } = useQuery(
    ['bi-calendar', bsPeriod, selectedDate, storeInfoId],
    async () => {
      const response = await biAPI.getCalendar({
        bsPeriod,
        month: bsPeriod,
        selectedDate,
        ...(storeInfoId ? { storeInfoId } : {}),
      });
      return response.data?.data || response.data;
    },
    { refetchOnWindowFocus: false, staleTime: 60 * 1000 }
  );

  useEffect(() => {
    if (data?.bsPeriod && data.bsPeriod !== bsPeriod) {
      setBsPeriod(data.bsPeriod);
    }
  }, [data?.bsPeriod, bsPeriod]);

  const tasks = data?.tasks || [];
  const summary = data?.summary || {};
  const eventsByDate = summary.eventsByDate || {};
  const bsCalendar = data?.bsCalendar || null;
  const monthDays = bsCalendar?.days || [];
  const monthGrid = useMemo(() => buildBsMonthGrid(monthDays), [monthDays]);
  const monthLabel = bsCalendar?.label || formatBsPeriod(bsPeriod);
  const monthName = bsCalendar?.bsMonthNameEn || String(monthLabel).replace(/\s+\d{4}\s+BS$/i, '');
  const monthTotalTasks = summary.monthTotalTasks ?? summary.monthEventCount ?? 0;
  const pendingTasks = summary.pendingTasks || [];

  const selectedDayTasks = visibleDayTasks(eventsByDate[selectedDate] || []);
  const todayTasks = visibleDayTasks(eventsByDate[todayKey] || []);
  const todayCompleted = todayTasks.filter((task) => task.status === 'completed' || task.isCompleted).length;
  const todayProgress = todayTasks.length ? Math.round((todayCompleted / todayTasks.length) * 100) : 0;

  const reminders = useMemo(
    () => tasks
      .filter((task) => (
        task.status === 'pending'
        && (taskDueDate(task) || '') > todayKey
        && (task.scheduledDate || '') <= shiftAdDateKey(todayKey, 30)
      ))
      .sort((a, b) => String(a.scheduledDate || '').localeCompare(String(b.scheduledDate || '')))
      .slice(0, 8),
    [tasks, todayKey]
  );

  const goToToday = async () => {
    setSelectedDate(todayKey);
    const response = await biAPI.getCalendar({
      selectedDate: todayKey,
      ...(storeInfoId ? { storeInfoId } : {}),
    });
    const payload = response.data?.data || response.data;
    if (payload?.bsPeriod) setBsPeriod(payload.bsPeriod);
  };

  const invalidateCalendar = async () => {
    await queryClient.invalidateQueries('bi-calendar');
    await queryClient.invalidateQueries('notifications');
  };

  const saveMutation = useMutation(
    (payload) => biAPI.createCalendarTask(payload),
    {
      onSuccess: async () => {
        toast.success('Task scheduled.');
        setShowModal(false);
        setTaskForm(defaultTaskForm(selectedDate));
        await invalidateCalendar();
      },
      onError: (error) => toast.error(error?.response?.data?.message || 'Failed to save task'),
    }
  );

  const actionMutation = useMutation(
    ({ id, payload }) => biAPI.updateCalendarTask(id, payload),
    {
      onSuccess: async (_data, variables) => {
        const action = variables?.payload?.action;
        toast.success(
          action === 'complete'
            ? 'Task completed.'
            : action === 'cancel'
              ? 'Task cancelled.'
              : 'Task rescheduled.'
        );
        setActionTask(null);
        await invalidateCalendar();
      },
      onError: (error) => toast.error(error?.response?.data?.message || 'Failed to update task'),
    }
  );

  const openCreateModal = () => {
    setTaskForm(defaultTaskForm(selectedDate));
    setShowModal(true);
  };

  const openPendingAction = (task) => {
    setActionTask(task);
    const next = (task.scheduledDate || todayKey) > todayKey
      ? task.scheduledDate
      : shiftAdDateKey(todayKey, 1);
    setRescheduleDate(next > maxRescheduleDate ? maxRescheduleDate : next);
  };

  const handleSaveTask = () => {
    if (!taskForm.title.trim()) {
      toast.error('Task title is required.');
      return;
    }
    saveMutation.mutate({
      title: taskForm.title,
      description: taskForm.description,
      scheduledDate: taskForm.scheduledDate,
      scheduledEndDate: taskForm.scheduledEndDate || taskForm.scheduledDate,
      ...(storeInfoId ? { storeInfoId } : {}),
    });
  };

  const handleComplete = () => {
    if (!actionTask) return;
    actionMutation.mutate({ id: actionTask.id, payload: { action: 'complete', status: 'completed' } });
  };

  const handleCancelTask = () => {
    if (!actionTask) return;
    actionMutation.mutate({ id: actionTask.id, payload: { action: 'cancel', status: 'cancelled' } });
  };

  const handleReschedule = () => {
    if (!actionTask) return;
    if (!rescheduleDate) {
      toast.error('Pick a reschedule date.');
      return;
    }
    if (rescheduleDate <= todayKey) {
      toast.error('Reschedule date must be after today.');
      return;
    }
    if (rescheduleDate > maxRescheduleDate) {
      toast.error('Reschedule is limited to within 1 month.');
      return;
    }
    actionMutation.mutate({
      id: actionTask.id,
      payload: {
        action: 'reschedule',
        status: 'pending',
        scheduledDate: rescheduleDate,
      },
    });
  };

  if (!canManage) {
    return (
      <div className={biPanel}>
        <p className="text-sm text-slate-500">Access denied.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">Task Scheduling</p>
          <h2 className="mt-1 text-[28px] font-bold tracking-tight text-slate-900">Calendar</h2>
          <p className="mt-1 text-sm text-slate-500">All-day plans by Nepali (BS) month.</p>
        </div>
        <button
          type="button"
          onClick={openCreateModal}
          className="inline-flex items-center gap-2 rounded-lg bg-[#6C5CE7] px-5 py-3 text-sm font-semibold text-white shadow-[0_12px_30px_rgba(108,92,231,0.35)] transition hover:bg-[#5b4bd6]"
        >
          <Plus className="h-4 w-4" />
          Schedule Task
        </button>
      </div>

      <div className="grid gap-4 xl:grid-cols-[260px_minmax(0,1fr)_260px]">
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200/80 bg-white p-5 shadow-[0_8px_30px_rgba(15,23,42,0.04)]">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-base font-semibold text-slate-900">Today&apos;s Tasks</h3>
              <span className="rounded-full bg-[#F3F0FF] px-2.5 py-0.5 text-xs font-semibold text-[#6C5CE7]">
                {todayTasks.length}
              </span>
            </div>
            <div className="space-y-3">
              {todayTasks.length === 0 ? (
                <p className="text-sm text-slate-500">No tasks scheduled for today.</p>
              ) : todayTasks.map((task) => {
                const meta = getColorMeta(task.color);
                const done = task.status === 'completed' || task.isCompleted;
                return (
                  <div key={task.id} className="flex items-start gap-3">
                    <span className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${meta.dot}`} />
                    <div className="min-w-0 flex-1">
                      <p className={`truncate text-sm font-medium ${done ? 'text-slate-400 line-through' : 'text-slate-800'}`}>
                        {task.title}
                      </p>
                      <p className="text-xs text-slate-400">All day{done ? ' · Completed' : ''}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-5">
              <div className="mb-2 flex items-center justify-between text-xs text-slate-500">
                <span>{todayCompleted} of {todayTasks.length || 0} completed</span>
                <span className="font-semibold text-slate-700">{todayProgress}%</span>
              </div>
              <div className="h-2.5 overflow-hidden rounded-full bg-slate-100">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-[#8B7CF7] to-[#6C5CE7] transition-all"
                  style={{ width: `${todayProgress}%` }}
                />
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200/80 bg-white p-5 shadow-[0_8px_30px_rgba(15,23,42,0.04)]">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell className="h-4 w-4 text-[#F3A060]" />
                <h3 className="text-base font-semibold text-slate-900">Reminders</h3>
              </div>
              {reminders.length > 0 ? (
                <span className="rounded-full bg-[#FFF1E6] px-2.5 py-0.5 text-xs font-semibold text-[#C46A2B]">
                  {reminders.length}
                </span>
              ) : null}
            </div>
            <div className="space-y-3">
              {reminders.length === 0 ? (
                <p className="text-sm text-slate-500">No upcoming reminders in the next month.</p>
              ) : reminders.map((task) => {
                const meta = getColorMeta(task.color);
                const daysLeft = daysUntilDateKey(todayKey, taskDueDate(task));
                return (
                  <div key={task.id} className="flex items-start gap-3">
                    <span className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${meta.dot}`} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-slate-900">{task.title}</p>
                      <p className="text-xs text-slate-400">
                        {formatTaskDateRange(task)} · {formatDaysLeftLabel(daysLeft)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200/80 bg-white p-5 shadow-[0_10px_40px_rgba(15,23,42,0.05)]">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-[26px] font-bold tracking-tight text-slate-900">{monthLabel}</h3>
              <p className="mt-1 text-sm text-slate-500">
                {bsCalendar?.startDate && bsCalendar?.endDate
                  ? `AD ${bsCalendar.startDate} to ${bsCalendar.endDate}`
                  : 'Nepali month calendar'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setBsPeriod((current) => shiftBsPeriod(current, -1))}
                className="rounded-md border border-slate-200 p-2.5 text-slate-600 transition hover:bg-slate-50"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={goToToday}
                className="rounded-md border border-slate-200 px-3.5 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Today
              </button>
              <button
                type="button"
                onClick={() => setBsPeriod((current) => shiftBsPeriod(current, 1))}
                className="rounded-md border border-slate-200 p-2.5 text-slate-600 transition hover:bg-slate-50"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-7 gap-2 text-center text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
            {WEEKDAYS.map((day) => <div key={day} className="py-1">{day}</div>)}
          </div>

          <div className="mt-2 grid grid-cols-7 gap-2">
            {monthGrid.map((cell) => {
              if (!cell.inMonth) {
                return <div key={cell.key} className="min-h-[104px] rounded-md" />;
              }

              const dayEvents = visibleDayTasks(eventsByDate[cell.adDate] || []);
              const isSelected = cell.adDate === selectedDate;
              const isToday = cell.adDate === todayKey;
              const visibleEvents = isSelected ? dayEvents.slice(0, 3) : dayEvents.slice(0, 2);
              const extraCount = Math.max(0, dayEvents.length - visibleEvents.length);

              return (
                <button
                  key={cell.key}
                  type="button"
                  onClick={() => setSelectedDate(cell.adDate)}
                  className={`group relative min-h-[104px] rounded-md border p-2.5 text-left transition ${
                    isSelected
                      ? 'border-transparent bg-gradient-to-br from-[#8B7CF7] to-[#5B4BD6] text-white shadow-[0_16px_36px_rgba(108,92,231,0.35)]'
                      : isToday
                        ? 'border-[#D9D2FF] bg-[#F7F5FF] text-slate-900 hover:border-[#C7BBFF]'
                        : 'border-slate-100 bg-[#FAFBFC] text-slate-900 hover:border-slate-200 hover:bg-white'
                  }`}
                >
                  <div className="flex items-start justify-between gap-1">
                    <div>
                      <div className={`text-[15px] font-bold leading-none ${isSelected ? 'text-white' : 'text-slate-900'}`}>
                        {cell.bsDay}
                      </div>
                      <div className={`mt-1 text-[10px] font-medium ${isSelected ? 'text-white/75' : 'text-slate-400'}`}>
                        {String(cell.adDate || '').slice(5)}
                      </div>
                    </div>
                  </div>

                  {dayEvents.length > 0 ? (
                    <div className="mt-2.5 space-y-1">
                      {visibleEvents.map((event) => {
                        const meta = getColorMeta(event.color);
                        return (
                          <div
                            key={event.id}
                            className={`truncate rounded-md px-2 py-0.5 text-[10px] font-semibold ${
                              isSelected ? meta.selectedPill : meta.pill
                            }`}
                          >
                            {event.title}
                          </div>
                        );
                      })}
                      {extraCount > 0 ? (
                        <div className={`text-[10px] font-semibold ${isSelected ? 'text-white/80' : 'text-slate-400'}`}>
                          +{extraCount} more
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </button>
              );
            })}
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-slate-100 pt-4">
            {LEGEND.map((item) => (
              <div key={item.key} className="inline-flex items-center gap-2 text-xs font-medium text-slate-600">
                <span className={`h-2.5 w-2.5 rounded-full ${item.dot}`} />
                {item.label}
              </div>
            ))}
          </div>

          {isLoading ? <p className="mt-3 text-sm text-slate-500">Loading calendar...</p> : null}
          {!isLoading && (isError || !monthDays.length) ? (
            <p className="mt-3 text-sm text-amber-700">
              {isError
                ? (error?.response?.data?.message || error?.message || 'Failed to load calendar.')
                : 'Calendar month could not be loaded. Refresh the page, or check that the backend is running the latest calendar code.'}
            </p>
          ) : null}
        </div>

        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200/80 bg-white p-5 shadow-[0_8px_30px_rgba(15,23,42,0.04)]">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Nepali month</p>
                <h3 className="mt-1 text-lg font-bold text-slate-900">{monthName}</h3>
              </div>
              <CalendarDays className="h-4 w-4 text-slate-400" />
            </div>
            <div className="rounded-lg bg-[#F7F5FF] px-4 py-4 text-center">
              <p className="text-3xl font-bold tracking-tight text-[#5B4BD6]">{monthTotalTasks}</p>
              <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Total tasks</p>
            </div>
            {selectedDayTasks.length > 0 ? (
              <div className="mt-4 space-y-2 border-t border-slate-100 pt-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Selected day</p>
                {selectedDayTasks.slice(0, 4).map((task) => {
                  const meta = getColorMeta(task.color);
                  return (
                    <div key={task.id} className="flex items-center gap-2 rounded-md bg-slate-50 px-3 py-2">
                      <span className={`h-2 w-2 rounded-full ${meta.dot}`} />
                      <span className="truncate text-xs font-medium text-slate-700">{task.title}</span>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>

          <div className="rounded-xl border border-slate-200/80 bg-white p-5 shadow-[0_8px_30px_rgba(15,23,42,0.04)]">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock3 className="h-4 w-4 text-[#C46A2B]" />
                <h3 className="text-base font-semibold text-slate-900">Pending</h3>
              </div>
              <span className="rounded-full bg-[#FFF1E6] px-2.5 py-0.5 text-xs font-semibold text-[#C46A2B]">
                {pendingTasks.length}
              </span>
            </div>

            <div className="max-h-[28rem] space-y-2 overflow-auto">
              {pendingTasks.length === 0 ? (
                <div className="rounded-lg bg-[#FFF8F1] px-4 py-6 text-center">
                  <p className="text-sm font-medium text-slate-700">No pending tasks</p>
                  <p className="mt-1 text-xs text-slate-500">Due or overdue items appear here.</p>
                </div>
              ) : pendingTasks.map((task) => {
                const meta = getColorMeta(task.color);
                return (
                  <button
                    key={task.id}
                    type="button"
                    onClick={() => openPendingAction(task)}
                    className="flex w-full items-start gap-3 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2.5 text-left transition hover:border-[#D9D2FF] hover:bg-[#F7F5FF]"
                  >
                    <span className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${meta.dot}`} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-slate-900">{task.title}</p>
                      <p className="text-xs text-slate-500">
                        Due {formatTaskDateRange(task)}
                        {taskDueDate(task) < todayKey ? ' · Overdue' : task.scheduledDate <= todayKey ? ' · In progress' : ' · Upcoming'}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {showModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
          <div className="w-full max-w-lg rounded-xl border border-slate-200 bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-900">Schedule All-day Task</h3>
              <button type="button" onClick={() => setShowModal(false)} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Title</label>
                <input
                  className="input"
                  value={taskForm.title}
                  onChange={(e) => setTaskForm((c) => ({ ...c, title: e.target.value }))}
                  placeholder="Team plan, report due..."
                />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Start date</label>
                  <input
                    type="date"
                    className="input"
                    value={taskForm.scheduledDate}
                    onChange={(e) => setTaskForm((c) => ({
                      ...c,
                      scheduledDate: e.target.value,
                      scheduledEndDate: c.scheduledEndDate < e.target.value ? e.target.value : c.scheduledEndDate,
                    }))}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">End date</label>
                  <input
                    type="date"
                    className="input"
                    min={taskForm.scheduledDate}
                    value={taskForm.scheduledEndDate}
                    onChange={(e) => setTaskForm((c) => ({ ...c, scheduledEndDate: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Notes</label>
                <textarea
                  className="input min-h-[88px]"
                  value={taskForm.description}
                  onChange={(e) => setTaskForm((c) => ({ ...c, description: e.target.value }))}
                  placeholder="Optional details"
                />
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button
                type="button"
                className="rounded-lg bg-[#6C5CE7] px-4 py-2 text-sm font-semibold text-white hover:bg-[#5b4bd6] disabled:opacity-50"
                onClick={handleSaveTask}
                disabled={saveMutation.isLoading}
              >
                {saveMutation.isLoading ? 'Saving...' : 'Save Task'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {actionTask ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
          <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-900">Pending task</h3>
              <button type="button" onClick={() => setActionTask(null)} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100">
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm font-semibold text-slate-900">{actionTask.title}</p>
            <p className="mt-1 text-xs text-slate-500">Due {formatTaskDateRange(actionTask)}</p>
            {actionTask.description ? (
              <p className="mt-3 text-sm text-slate-600">{actionTask.description}</p>
            ) : null}

            <div className="mt-4">
              <label className="mb-1 block text-sm font-medium text-slate-700">Reschedule date</label>
              <input
                type="date"
                className="input"
                min={shiftAdDateKey(todayKey, 1)}
                max={maxRescheduleDate}
                value={rescheduleDate}
                onChange={(e) => setRescheduleDate(e.target.value)}
              />
              <p className="mt-1 text-[11px] text-slate-400">Within 1 month from today (max {maxRescheduleDate}).</p>
            </div>

            <div className="mt-5 grid gap-2 sm:grid-cols-3">
              <button
                type="button"
                className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-600 hover:bg-rose-100 disabled:opacity-50"
                onClick={handleCancelTask}
                disabled={actionMutation.isLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
                onClick={handleComplete}
                disabled={actionMutation.isLoading}
              >
                Complete
              </button>
              <button
                type="button"
                className="rounded-lg bg-[#6C5CE7] px-3 py-2 text-sm font-semibold text-white hover:bg-[#5b4bd6] disabled:opacity-50"
                onClick={handleReschedule}
                disabled={actionMutation.isLoading}
              >
                Reschedule
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default BiCalendar;
